/**
 * OUTFIT — Purchase Tracking Service
 *
 * Flux complet :
 * 1. Utilisateur clique "Voir sur Zalando" → lien affilié AWIN
 * 2. Outfit ouvre Zalando avec deep link + paramètres de tracking
 * 3. Zalando notifie AWIN après paiement (webhook automatique)
 * 4. AWIN notifie notre Lambda /awin-webhook avec les détails de la commande
 * 5. Lambda ajoute automatiquement l'article à la garde-robe + notifications
 *
 * En attendant le webhook AWIN (nécessite approbation programme),
 * on utilise le "return URL" pattern : l'app détecte le retour
 * depuis Zalando et propose d'ajouter à la garde-robe.
 */

import { Linking, Alert, Platform } from 'react-native';
import * as WebBrowser from 'expo-web-browser';
// v1.12.117 — wrapper avec fallback localStorage sur web
import * as SecureStore from '../utils/secureStore';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { AWS_CONFIG } from '../config/aws_config';
// v1.10.91 : utilitaire pour proxifier les CDN partenaires bloqués
import { getProxiedImageUrl, isBlockedDomain } from '../utils/imageProxy';

// Helper pour récupérer le token Cognito (via SecureStore — comme AuthContext)
// Utilisé comme fallback quand le user ne fournit pas de token dans l'objet user
async function getAuthToken() {
  try {
    return await SecureStore.getItemAsync('outfit_access');
  } catch (e) {
    return null;
  }
}


const API = AWS_CONFIG.apiUrl;

// v1.13.62 — fetch avec TIMEOUT (AbortController) pour éviter que l'ajout au
// dressing reste bloqué indéfiniment si un endpoint ne répond pas.
async function _fetchTimeout(url, opts = {}, ms = 20000) {
  const ctl = new AbortController();
  const to = setTimeout(() => { try { ctl.abort(); } catch {} }, ms);
  try {
    return await fetch(url, { ...opts, signal: ctl.signal });
  } finally { clearTimeout(to); }
}

/* ══════════════════════════════════════════════════════════
   1. OUVRIR LE PRODUIT SUR ZALANDO / ZARA
   Sauvegarde le produit en attente et ouvre le navigateur
══════════════════════════════════════════════════════════ */
export async function openProductForPurchase(product, userOrId, options) {
  // v1.13.76 — WEB : sur le navigateur de l'user, window.open(url) DIRECT (même
  //   synchrone) est bloqué par le pop-up blocker, MAIS pré-ouvrir un onglet
  //   blanc EN TOUTE PREMIÈRE INSTRUCTION (encore dans le geste) fonctionne
  //   (confirmé : .69 ouvrait, .75 sans pré-open n'ouvrait rien). On restaure
  //   donc le pré-open. Différence clé vs .69 : plus AUCUN await avant l'injection
  //   de l'URL (tout est synchrone) → l'onglet reçoit son URL dans le même tick.
  let _webWin = null;
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    try { _webWin = window.open('about:blank', '_blank'); } catch {}
  }
  // v1.13.73 — RÉÉCRITURE : TOUT le nécessaire à l'ouverture est calculé de façon
  //   SYNCHRONE (le SID riche est générable en synchrone) ; le tracking (token,
  //   /cj/track-click, sovrn, analytics) part en ARRIÈRE-PLAN et n'affecte JAMAIS
  //   l'ouverture.
  try {
    const opts = options || {};

    // v1.13.80 — Normaliser les champs URL : un favori (surtout rechargé du
    //   backend / anciennes versions) peut porter l'URL en snake_case
    //   (affiliate_url, original_url, product_url) → buildTrackedUrl ne regardait
    //   que le camelCase → aucune URL trouvée → "URL invalide" → redirection KO
    //   depuis les favoris. On comble les champs camelCase manquants.
    if (product && typeof product === 'object') {
      if (!product.affiliateUrl && (product.affiliate_url || product.affiliateURL)) product.affiliateUrl = product.affiliate_url || product.affiliateURL;
      if (!product.originalUrl && (product.original_url || product.product_url || product.productUrl)) product.originalUrl = product.original_url || product.product_url || product.productUrl;
      if (!product.url && (product.product_url || product.productUrl || product.link)) product.url = product.product_url || product.productUrl || product.link;
    }

    // Accepter soit un userId string, soit un objet user complet
    let userId = null;
    let userObj = null;
    if (typeof userOrId === 'string') {
      userId = userOrId;
    } else if (userOrId && typeof userOrId === 'object') {
      userObj = userOrId;
      userId = userOrId.id || userOrId.sub || userOrId.userId || null;
    }
    const safeUserId = typeof userId === 'string' && userId.length > 0 ? userId : 'guest';

    // ═══ SID RICHE — SYNCHRONE (aucun await avant l'ouverture) ═══
    // Format : userId_productId_timestamp_random. Injecté dans l'URL ET
    // renvoyé au backend (même SID) pour le matching commission.
    let richSid = safeUserId;
    try {
      const { generateSid } = require('./CJTrackingService');
      richSid = generateSid(safeUserId, product?.id) || safeUserId;
    } catch (e) { /* fallback safeUserId */ }

    // ═══ URL trackée — SYNCHRONE ═══
    let finalUrl = buildTrackedUrl(product, richSid);
    if (!finalUrl || typeof finalUrl !== 'string' || !finalUrl.startsWith('http')) {
      const raw = product.affiliateUrl || product.url || product.originalUrl || '';
      if (typeof raw === 'string' && raw.startsWith('http')) {
        console.warn('openProductForPurchase: URL trackée invalide → repli URL brute');
        finalUrl = raw;
      } else {
        console.warn('openProductForPurchase: URL invalide', finalUrl);
        if (_webWin && !_webWin.closed) { try { _webWin.close(); } catch {} }
        return { success: false, error: 'URL invalide' };
      }
    }

    const isCjLink = /anrdoezrs\.net|kqzyfj\.com|jdoqocy\.com|tkqlhce\.com|lduhtrp\.net|dpbolvw\.net/i.test(finalUrl);

    // ═══ OUVERTURE — SYNCHRONE, dans le geste utilisateur ═══
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      // v1.13.77 — BUG REDIRECTION : on ré-emballait finalUrl dans
      //   redirect.viglink.com. Or buildTrackedUrl renvoie DÉJÀ une URL trackée
      //   (linksynergy/Rakuten, skimlinks, CJ, awin, ou sovrn pour les URLs
      //   marchandes brutes). Le double-wrap viglink d'un lien d'affiliation
      //   (ex. click.linksynergy.com des produits Coutr/Suit Negozi) CASSAIT la
      //   redirection → l'onglet s'ouvrait mais n'atteignait jamais le marchand
      //   (« pas de redirection vers le site partenaire »). On ouvre finalUrl
      //   DIRECTEMENT. Le ping Sovrn de comptage se fait déjà en arrière-plan.
      const openUrl = finalUrl;
      // On injecte l'URL dans l'onglet PRÉ-OUVERT (dans le geste) → contourne le
      // bloqueur de pop-up. Comme tout est synchrone, l'injection se fait dans le
      // même tick que le clic. Repli : window.open direct si le pré-open a échoué.
      if (_webWin && !_webWin.closed) {
        try { _webWin.location.href = openUrl; }
        catch { try { _webWin.location = openUrl; } catch {} }
        // Isoler l'onglet marchand du contexte de l'app (best-effort ; cross-origin
        // après navigation → souvent SecurityError, d'où le try/catch).
        try { _webWin.opener = null; } catch {}
      } else {
        try { window.open(openUrl, '_blank', 'noopener,noreferrer'); }
        catch (e) { console.warn('[purchase] window.open échec:', e?.message); }
      }
    } else {
      // ═══ MOBILE ═══
      // v1.13.78 — FIX "favoris casse toute la redirection jusqu'au redémarrage" :
      //   dans favoris, WebBrowser.openBrowserAsync s'ouvrait PENDANT la fermeture
      //   du modal d'action → session in-app ORPHELINE côté natif → tous les
      //   openBrowserAsync suivants (partout dans l'app) échouaient/pendouillaient
      //   jusqu'au redémarrage. Correctif :
      //     1. dismissBrowser() AVANT chaque ouverture → purge toute session bloquée.
      //     2. Repli Linking.openURL (navigateur SYSTÈME, sans état de session) si
      //        WebBrowser échoue → ne peut jamais rester bloqué.
      (async () => {
        // Purge par sécurité une éventuelle session in-app orpheline.
        // v1.13.80 — NON-bloquant (pas d'await) : sur certaines plateformes,
        //   dismissBrowser() ne résout jamais quand aucun navigateur n'est ouvert
        //   → un await ICI bloquait l'ouverture et RIEN ne s'ouvrait (favoris et +).
        try { WebBrowser.dismissBrowser?.().catch(() => {}); } catch {}
        // PRIMAIRE = Linking (navigateur SYSTÈME) : stateless, ne peut pas rester
        // « présenté » et bloquer les ouvertures suivantes (cf. bug favoris), et
        // meilleure persistance cookies pour l'attribution affiliée.
        try {
          await Linking.openURL(finalUrl);
        } catch (e) {
          console.warn('[purchase] Linking KO → WebBrowser:', e?.message);
          try {
            await WebBrowser.openBrowserAsync(finalUrl);
          } catch (e2) {
            const fallbackUrl = product.originalUrl || product.url;
            if (fallbackUrl && fallbackUrl !== finalUrl && !/anrdoezrs|kqzyfj|jdoqocy|tkqlhce/i.test(fallbackUrl)) {
              try { await Linking.openURL(fallbackUrl); }
              catch (e3) { console.warn('[purchase] all mechanisms failed:', e3?.message); }
            }
          }
        }
      })();
    }

    // ═══ TRACKING EN ARRIÈRE-PLAN (n'affecte JAMAIS l'ouverture) ═══
    setTimeout(() => {
      (async () => {
        // Résoudre un token si l'appelant n'en a pas fourni (pour /cj/track-click)
        let uObj = userObj;
        try {
          if (uObj && !uObj.accessToken) {
            const t = await getAuthToken();
            if (t) uObj = { ...uObj, accessToken: t };
          } else if (!uObj && safeUserId !== 'guest') {
            const t = await getAuthToken();
            if (t) uObj = { id: safeUserId, accessToken: t };
          }
        } catch {}
        // /cj/track-click AVEC le MÊME richSid (matching commission)
        try {
          const { trackProductClick } = require('./CJTrackingService');
          await trackProductClick(product, uObj || { id: safeUserId }, opts.screen || 'unknown', richSid);
        } catch (e) { console.warn('[CJTracking] skip:', e?.message); }
        // Ping Sovrn (fire-and-forget)
        try {
          const sovrn = AFFILIATE_CONFIG.sovrn;
          if (sovrn.active && sovrn.apiKey) {
            const productUrl = product.originalUrl || product.url || finalUrl;
            const sovrnPingUrl = 'https://api.viglink.com/api/click'
              + '?key='  + sovrn.apiKey
              + '&u='    + encodeURIComponent(productUrl)
              + '&ref='  + encodeURIComponent('outfit-app-mobile')
              + '&cuid=' + encodeURIComponent(safeUserId.slice(0, 16))
              + '&loc='  + encodeURIComponent('outfit://catalogue');
            fetch(sovrnPingUrl, { method: 'GET' }).catch(() => {});
          }
        } catch {}
        trackClick(product, safeUserId).catch(() => {});
        savePendingProduct(product).catch(() => {});
        // GA4 : event "view_partner"
        try {
          const { trackEvent } = require('./AnalyticsService');
          trackEvent('view_partner', {
            product_id: String(product?.id || '').slice(0, 100),
            partner: product?.partner || 'unknown',
            brand: product?.brand || null,
            category: product?.category || null,
            price: typeof product?.price === 'number' ? product.price : parseFloat(product?.price) || 0,
            currency: product?.currency || 'EUR',
            screen: opts.screen || 'unknown',
            is_cj: isCjLink,
          });
        } catch {}
      })();
    }, 0);

    return { success: true, url: finalUrl };
  } catch (e) {
    console.error('openProductForPurchase:', e);
    return { success: false, error: e.message };
  }
}

/* ══════════════════════════════════════════════════════════
   TRACKER UN CLIC PARTENAIRE (stockage local)
══════════════════════════════════════════════════════════ */
export async function trackClick(product, userId) {
  // 1. Stockage local (rapide, toujours disponible)
  try {
    const raw     = await AsyncStorage.getItem('outfit_click_history');
    const history = raw ? JSON.parse(raw) : [];
    history.unshift({
      ts:       Date.now(),
      partner:  product.partner || 'unknown',
      name:     product.name || '',
      price:    product.price || 0,
      id:       product.id || '',
    });
    // Garder max 500 clics
    await AsyncStorage.setItem('outfit_click_history', JSON.stringify(history.slice(0, 500)));
  } catch(e) {}

  // 2. Envoi au backend pour analytics + matching commissions (fire-and-forget)
  try {
    const token = await getAuthToken();
    if (!token) return;
    // Extraire le link ID CJ depuis l'URL affiliée si présent
    let cjLinkId = null;
    const affUrl = product.affiliateUrl || product.url || '';
    const m = affUrl.match(/click-\d+-(\d+)/);
    if (m) cjLinkId = m[1];
    fetch(`${API}/track-click`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        partner:     product.partner || 'unknown',
        productId:   product.id || '',
        productName: product.name || '',
        price:       product.price || 0,
        currency:    product.currency || 'EUR',
        affiliateUrl: affUrl,
        productUrl:  product.originalUrl || product.url || '',
        cjLinkId,
        sid:         userId || product.sid || '',
      }),
    }).catch(() => {}); // silencieux si offline
  } catch(e) {}
}

/* ══════════════════════════════════════════════════════════
   2. CONSTRUIRE L'URL AFFILIÉE AVEC TRACKING
   Ajoute le click reference AWIN pour identifier l'utilisateur
══════════════════════════════════════════════════════════ */
// ══ Configuration réseaux affiliation ══════════════════════
// Priorité : AWIN (si approuvé) → Tradedoubler → Skimlinks → URL directe

const AFFILIATE_CONFIG = {
  awin: {
    publisherId: '2854419',
    active: true, // Mettre false si pas encore approuvé
    programs: {
      zalando: '7476',   // ⚠️ En attente approbation
      asos:    '11949',  // ⚠️ En attente approbation
      zara:    '18532',
      hm:      null,     // Chercher sur ui.awin.com
    },
    buildUrl: function(productUrl, mid, clickRef) {
      return 'https://www.awin1.com/cread.php'
        + '?awinaffid=2854419'
        + '&awinmid=' + mid
        + '&clickref=' + clickRef
        + '&ued=' + encodeURIComponent(productUrl);
    },
  },
  tradedoubler: {
    publisherId: '',     // 👉 Remplir après inscription tradedoubler.com
    active: false,       // Activer après inscription
    programs: {
      zalando: '',       // 👉 Remplir après approbation Zalando TD
      asos:    '',       // 👉 Remplir après approbation ASOS TD
      hm:      '',       // 👉 Remplir après approbation H&M TD
      zara:    '',       // 👉 Remplir après approbation Zara TD
      mango:   '',
    },
    buildUrl: function(productUrl, programId, clickRef, publisherId) {
      // Format Tradedoubler : td.doubleclick.net
      return 'https://clk.tradedoubler.com/click'
        + '?p=' + programId
        + '&a=' + publisherId
        + '&url=' + encodeURIComponent(productUrl)
        + '&epi=' + clickRef;
    },
  },
  cj: {
    // ═══ v1.7.73 : CJ activé pour les partenaires CJ joined (TOUS, etc.) ═══
    // Website ID = PID Outfit ("To be Smart" = 7931766 dans notre compte CJ).
    // Le PID est utilisé dans l'URL : https://www.dpbolvw.net/click-{PUB}-{LINK}
    //   où PUB = 101729219 (CJ Publisher Id de To be Smart)
    //   et LINK = AID du programme (par advertiser)
    websiteId: '101729219',  // CJ Publisher ID de "To be Smart"
    active: true,
    programs: {
      // TOUS deeplink AID — lien texte deeplink-enabled (autorise ?url=)
      // AID 17279824 = "TOUS PT -10% en APP" (lien texte)
      // Confirmé deeplink-enabled : structure dpbolvw.net/click-{PUB}-{AID}?url=...
      // À changer si vous voulez un autre AID (n'importe quel lien texte TOUS marche).
      tous: '17279824',
      hm:   '',          // H&M programme CJ officiel
    },
    buildUrl: function(productUrl, programId, clickRef, websiteId) {
      // Format CJ deeplink : https://www.dpbolvw.net/click-{PUB}-{AID}?url=...&sid=...
      // dpbolvw.net est l'un des 6 domaines de tracking CJ (les 6 sont équivalents)
      return 'https://www.dpbolvw.net/click-' + websiteId + '-' + programId
        + '?url=' + encodeURIComponent(productUrl)
        + '&sid=' + encodeURIComponent(clickRef);
    },
  },

  // ── Impact.com — H&M, ASOS, Farfetch — acceptation rapide ──
  // Inscription : impact.com → Publishers → Sign Up
  impact: {
    accountSid: '',      // 👉 Account SID après inscription impact.com
    active: false,
    programs: {
      hm:      '',       // H&M programme officiel Impact
      asos:    '',
      farfetch: '',
    },
    buildUrl: function(productUrl, programId, clickRef) {
      return 'https://go.impact.com/visit'
        + '?programid=' + programId
        + '&subid='     + encodeURIComponent(clickRef)
        + '&url='       + encodeURIComponent(productUrl);
    },
  },

  // ── Sovrn Commerce (ex-VigLink) — auto-approve ────────────
  // Alternative à Skimlinks, même modèle, auto-approuvé
  // Inscription : sovrn.com/commerce
  // v1.12.116 — clé renouvelée 2026-05 (compte mytenue.com)
  sovrn: {
    apiKey: '2ea6c250a81f2decfa3b9c05ab9617dd',
    active: true,
    buildUrl: function(productUrl, clickRef, apiKey) {
      return 'https://redirect.viglink.com'
        + '?key=' + apiKey
        + '&u='   + encodeURIComponent(productUrl)
        + '&ref=' + encodeURIComponent(clickRef);
    },
  },
};

// Ajoute le SID utilisateur à une URL CJ existante (click-PUB-LINK?url=...)
// Le SID est envoyé à CJ qui le retourne dans la Commission Detail API
// → on peut réconcilier la commission avec le clic précis (via cj_clicks.sid)
// Format SID : userId_productId_timestamp_random (généré par CJTrackingService)
function addCjSid(cjUrl, richSid) {
  if (!cjUrl || !richSid) return cjUrl;
  // Vérifier que c'est bien une URL CJ
  if (!/kqzyfj|anrdoezrs|jdoqocy|tkqlhce|lduhtrp|dpbolvw/i.test(cjUrl)) return cjUrl;
  // Si sid déjà présent, ne pas dupliquer
  if (cjUrl.includes('&sid=') || cjUrl.includes('?sid=')) return cjUrl;
  const separator = cjUrl.includes('?') ? '&' : '?';
  return cjUrl + separator + 'sid=' + encodeURIComponent(richSid);
}

// v1.12.564 — Rakuten LinkShare : injecter u1=SID pour tracker le click par user
// Pattern URL : https://click.linksynergy.com/deeplink?id=X&mid=Y&u1=SID&murl=...
// Le param u1 est renvoyé dans le webhook de vente → permet match click→commission.
function addRakutenSid(rakutenUrl, richSid) {
  if (!rakutenUrl || !richSid) return rakutenUrl;
  if (!rakutenUrl.includes('click.linksynergy.com')) return rakutenUrl;
  if (rakutenUrl.includes('&u1=') || rakutenUrl.includes('?u1=')) return rakutenUrl;
  const separator = rakutenUrl.includes('?') ? '&' : '?';
  return rakutenUrl + separator + 'u1=' + encodeURIComponent(richSid);
}

function buildTrackedUrl(product, userId) {
  // Helper : true si l'URL est déjà un deeplink CJ
  const isCjUrl = (u) => /anrdoezrs|tkqlhce|jdoqocy|kqzyfj|lduhtrp|dpbolvw/i.test(u);

  // ═══ PRIORITÉ 1 : Cashmere / iQueens / TOUS — SID utilisateur OBLIGATOIRE pour commissions CJ
  // Format: URL CJ existante + &sid=COGNITO_SUB (utilisé pour réconcilier dans /sync-commissions)
  // On traite en premier AVANT le shortcut affiliateUrl générique
  if (product.partner === 'cashmere') {
    let cbUrl = product.affiliateUrl || product.url || product.originalUrl;
    if (cbUrl && cbUrl.startsWith('http')) {
      // Si l'URL n'est pas déjà CJ-wrappée (cas favori ancien), wrapper maintenant
      // pour que CJ tracke le clic. Sinon le user va direct sur cashmere.com → 0 commission.
      if (!isCjUrl(cbUrl)) {
        try {
          const { getCBEvergreen } = require('./CashmereService');
          cbUrl = getCBEvergreen(cbUrl);
        } catch (e) { console.warn('[CJ-cashmere] wrap failed:', e?.message); }
      }
      return addCjSid(cbUrl, userId);
    }
  }
  if (product.partner === 'iqueens') {
    let iqUrl = product.affiliateUrl || product.url || product.originalUrl;
    if (iqUrl && iqUrl.startsWith('http')) {
      // Si pas déjà CJ-wrappée → wrapper via getIQDeepUrl (sinon CJ ne tracke rien).
      if (!isCjUrl(iqUrl)) {
        try {
          const { getIQDeepUrl } = require('./IQueensService');
          iqUrl = getIQDeepUrl(iqUrl);
        } catch (e) { console.warn('[CJ-iqueens] wrap failed:', e?.message); }
      }
      return addCjSid(iqUrl, userId);
    }
  }
  // ═══ v1.7.73 : TOUS via CJ deeplink ═══
  // Si product.affiliateUrl est déjà construit (par le cron Lambda avec CJ_TOUS_DEEPLINK_AID),
  // on ajoute juste le SID. Sinon on construit le deeplink à la volée si AID dispo.
  if (product.partner === 'tous') {
    // Cas 1 : URL CJ déjà construite côté Lambda cron → ajouter SID
    const tousUrl = product.affiliateUrl || product.url || product.originalUrl;
    if (tousUrl && /click-\d+-\d+\?url=/i.test(tousUrl)) {
      // Déjà un deeplink CJ → juste ajouter SID
      return addCjSid(tousUrl, userId);
    }
    // Cas 2 : URL TOUS directe (pas de CJ wrap) → essayer de wrapper avec AFFILIATE_CONFIG.cj.programs.tous
    const cj = AFFILIATE_CONFIG.cj;
    if (tousUrl && tousUrl.startsWith('http') && cj.active && cj.programs.tous) {
      const clickRef = (userId || 'guest').slice(0,8) + '_' + (product.id || '').toString().slice(0,10) + '_' + Date.now();
      return cj.buildUrl(tousUrl, cj.programs.tous, clickRef, cj.websiteId);
    }
    // Cas 3 : pas d'AID configuré → URL directe sans tracking (warning visible côté backend)
    if (tousUrl && tousUrl.startsWith('http')) {
      console.warn('[CJ-TOUS] Pas de deeplink AID configuré. Clic sans tracking commission.');
      return tousUrl;
    }
  }

  // ═══ PRIORITÉ 2 : affiliateUrl Skimlinks déjà construit (autres partenaires)
  if (product.affiliateUrl && product.affiliateUrl.startsWith('http') &&
      (product.affiliateUrl.includes('redirectingat.com') || product.affiliateUrl.includes('skimlinks'))) {
    return product.affiliateUrl;
  }
  // v1.12.564 — Rakuten LinkShare : injecter u1=SID pour tracking par user
  // (Suit Negozi, COUTR, etc.)
  if (product.affiliateUrl && product.affiliateUrl.includes('click.linksynergy.com')) {
    return addRakutenSid(product.affiliateUrl, userId);
  }
  // affiliateUrl générique
  if (product.affiliateUrl && product.affiliateUrl.startsWith('http')) {
    return product.affiliateUrl;
  }

  // URL directe du produit
  const productUrl = product.originalUrl || product.affiliateUrl || product.url;
  if (!productUrl || !productUrl.startsWith('http')) {
    // Pas d'URL → chercher par nom sur le site partenaire
    const name = encodeURIComponent(product.name || '');
    if (product.partner === 'zalando') return `https://www.zalando.fr/recherche/?q=${name}`;
    if (product.partner === 'zara')    return `https://www.zara.com/fr/fr/search?searchTerm=${name}`;
    if (product.partner === 'amazon')  return `https://www.amazon.fr/s?k=${name}`;
    if (product.partner === 'asos')    return `https://www.asos.com/fr/search/?q=${name}`;
    if (product.partner === 'iqueens')  return `https://www.iqueens.fr/fr/recherche?q=${name}`;
    return null;
  }

  const productUrl2 = product.originalUrl || product.url;
  if (!productUrl2 || !productUrl2.startsWith('http')) {
    const name = encodeURIComponent(product.name || '');
    if (product.partner === 'zalando') return 'https://www.zalando.fr/recherche/?q=' + name;
    if (product.partner === 'asos')    return 'https://www.asos.com/fr/search/?q=' + name;
    if (product.partner === 'hm')      return 'https://www2.hm.com/fr_fr/search-results.html?q=' + name;
    return null;
  }

  const clickRef = (userId || 'guest').slice(0,8) + '_' + (product.id || '').toString().slice(0,10) + '_' + Date.now();

  // 3. AWIN si actif et programme disponible
  const awin = AFFILIATE_CONFIG.awin;
  if (awin.active) {
    const mid = awin.programs[product.partner];
    if (mid) return awin.buildUrl(productUrl2, mid, clickRef);
  }

  // 4. Tradedoubler si actif et programme disponible
  const td = AFFILIATE_CONFIG.tradedoubler;
  if (td.active && td.publisherId) {
    const programId = td.programs[product.partner];
    if (programId) return td.buildUrl(productUrl2, programId, clickRef, td.publisherId);
  }

  // 5. CJ si actif
  const cj = AFFILIATE_CONFIG.cj;
  if (cj.active && cj.websiteId) {
    const programId = cj.programs[product.partner];
    if (programId) return cj.buildUrl(productUrl2, programId, clickRef, cj.websiteId);
  }

  // 6. Impact.com si actif
  const impact = AFFILIATE_CONFIG.impact;
  if (impact.active && impact.accountSid) {
    const programId = impact.programs[product.partner];
    if (programId) return impact.buildUrl(productUrl2, programId, clickRef);
  }

  // 7. Sovrn Commerce si actif
  const sovrn = AFFILIATE_CONFIG.sovrn;
  if (sovrn.active && sovrn.apiKey) {
    return sovrn.buildUrl(productUrl2, clickRef, sovrn.apiKey);
  }

  // 8. Sovrn direct si pas d'autre réseau
  const sovrnDirect = AFFILIATE_CONFIG.sovrn;
  if (sovrnDirect.active && sovrnDirect.apiKey) {
    return sovrnDirect.buildUrl(productUrl2, clickRef, sovrnDirect.apiKey);
  }

  // 9. Fallback URL directe
  return productUrl2;
}

/* ══════════════════════════════════════════════════════════
   3. SAUVEGARDER LE PRODUIT EN ATTENTE
   Conservé localement pour proposer l'ajout garde-robe au retour
══════════════════════════════════════════════════════════ */
export async function savePendingProduct(product) {
  try {
    const pending = {
      product,
      timestamp:        Date.now(),
      externalOpenedAt: Date.now(), // heure exacte d'ouverture du navigateur
      status:           'clicked',
    };
    await AsyncStorage.setItem('outfit_pending_purchase', JSON.stringify(pending));
  } catch (e) {
    console.error('savePendingProduct:', e);
  }
}

export async function getPendingProduct() {
  try {
    const raw = await AsyncStorage.getItem('outfit_pending_purchase');
    if (!raw) return null;
    const data = JSON.parse(raw);
    // Expirer après 2 heures
    if (Date.now() - data.timestamp > 2 * 60 * 60 * 1000) {
      await AsyncStorage.removeItem('outfit_pending_purchase');
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

export async function clearPendingProduct() {
  await AsyncStorage.removeItem('outfit_pending_purchase');
}

/* ══════════════════════════════════════════════════════════
   4. DÉTECTER LE RETOUR DEPUIS ZALANDO/ZARA
   Déclenché quand l'app repasse au premier plan
══════════════════════════════════════════════════════════ */


/* ══════════════════════════════════════════════════════════
   5. CONFIRMER L'ACHAT + AJOUTER À LA GARDE-ROBE
   Appelé quand l'utilisateur confirme qu'il a acheté l'article
══════════════════════════════════════════════════════════ */
/* ══════════════════════════════════════════════════════════
   UPLOAD IMAGE PRODUIT → S3
   Télécharge l'image CDN du partenaire et la stocke sur S3
   pour avoir une URL durable indépendante du partenaire
══════════════════════════════════════════════════════════ */
async function uploadProductImageToS3(imageUrl, userToken) {
  if (!imageUrl || !userToken) return null;

  try {
    // 1. Obtenir une URL pré-signée S3 depuis Lambda
    const presignRes = await fetch(`${API}/wardrobe/upload-url`, {
      method:  'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${userToken}`,
      },
      body: JSON.stringify({ file_type: 'image/jpeg', source: 'partner_cdn' }),
    });
    if (!presignRes.ok) throw new Error(`presign ${presignRes.status}`);
    const { uploadUrl, publicUrl } = await presignRes.json();

    // 2. Télécharger l'image depuis le CDN partenaire
    const imgRes = await fetch(imageUrl, {
      headers: {
        'User-Agent': 'Mozilla/5.0',
        'Referer':    'https://www.zalando.fr/',
      },
    });
    if (!imgRes.ok) throw new Error(`img fetch ${imgRes.status}`);
    const blob = await imgRes.blob();

    // 3. Uploader sur S3
    const s3Res = await fetch(uploadUrl, {
      method:  'PUT',
      headers: { 'Content-Type': 'image/jpeg' },
      body:    blob,
    });
    if (!s3Res.ok) throw new Error(`s3 put ${s3Res.status}`);

    console.log('Image uploadée sur S3:', publicUrl);
    return publicUrl;
  } catch (e) {
    console.warn('uploadProductImageToS3:', e.message);
    return null; // fallback → URL CDN directe
  }
}

export async function confirmPurchaseAndAddToWardrobe(product, size, userToken, options = {}) {
  // v1.12.89 — options.pseudo : alias user (optionnel) stocké dans ai_metadata.pseudo
  const userPseudo = (options.pseudo || '').trim() || null;
  // v1.12.219 — options.onProgress : callback(phase) pour afficher la progression
  // phases : 'uploading' (upload image), 'analyzing' (analyse IA serveur)
  const onProgress = typeof options.onProgress === 'function' ? options.onProgress : () => {};
  try {
    if (!userToken) {
      throw new Error('Non connecté — veuillez vous reconnecter');
    }
    onProgress('uploading');

    // Mapper le produit Zalando/Zara → format garde-robe
    // Récupérer l'URL image directe (CDN Zalando/Zara)
    const imageUrl = product.image || product.images?.[0] || product.image_url || null;

    // v1.10.89 : Si pas d'image du tout, on log mais on continue (fallback emoji)
    if (!imageUrl) {
      console.warn('[PurchaseConfirm] ⚠️ produit sans image:', product.name, product.brand);
    }

    // Tenter d'uploader l'image sur S3 (timeout 8s pour éviter blocage)
    // v1.12.486 — Skip S3 upload sur web : CORS bloque le fetch du CDN partenaire.
    // L'app utilise directement l'URL CDN avec proxy wsrv.nl si domaine bloqué.
    const { Platform } = require('react-native');
    const IS_WEB = Platform.OS === 'web';
    let finalImageUrl = imageUrl; // par défaut : URL CDN d'origine
    if (imageUrl && userToken && !IS_WEB) {
      try {
        const uploadPromise = uploadProductImageToS3(imageUrl, userToken);
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('S3 upload timeout')), 8000)
        );
        const uploaded = await Promise.race([uploadPromise, timeoutPromise]);
        // v1.10.89 : on n'écrase finalImageUrl QUE si l'upload a réussi (publicUrl).
        // Si null/undefined, on garde la CDN URL d'origine au lieu de finir avec null.
        if (uploaded && typeof uploaded === 'string' && uploaded.length > 10) {
          finalImageUrl = uploaded;
          console.log('[PurchaseConfirm] ✓ image S3 ok:', uploaded.slice(0, 80));
        } else {
          console.warn('[PurchaseConfirm] ⚠️ S3 upload returned null, fallback CDN');
          finalImageUrl = imageUrl;
        }
      } catch (e) {
        console.warn('[PurchaseConfirm] S3 upload skipped (using CDN url):', e.message);
        finalImageUrl = imageUrl; // CDN URL directe comme fallback
      }
    }

    // v1.10.89 : Garde-fou final — finalImageUrl ne doit jamais être null si imageUrl existait
    if (imageUrl && !finalImageUrl) {
      console.warn('[PurchaseConfirm] ⚠️ finalImageUrl perdue, restauration CDN');
      finalImageUrl = imageUrl;
    }

    // ═══ v1.10.91 : Auto-proxy pour CDN partenaires bloqués (TOUS, Zara, Cashmere) ═══
    //
    // Cas du bug : produits TOUS qui s'affichent dans la modal d'achat (où le proxy
    // wsrv.nl est appliqué) mais PAS dans le dressing après ajout (où l'URL brute
    // était stockée). Résultat : icône emoji 👗 au lieu de la photo.
    //
    // Si l'upload S3 a échoué ET que le domaine source est connu pour bloquer,
    // on stocke l'URL proxifiée en DB (https://wsrv.nl/?url=...) qui s'affiche
    // partout sans modification du code de rendu.
    //
    // Si l'upload S3 a réussi → finalImageUrl pointe sur S3 → pas besoin de proxy.
    if (finalImageUrl && !finalImageUrl.includes('amazonaws.com') && isBlockedDomain(finalImageUrl)) {
      const proxified = getProxiedImageUrl(finalImageUrl, { width: 800 });
      if (proxified !== finalImageUrl) {
        console.log('[PurchaseConfirm] 🔀 CDN bloqué détecté, stockage URL proxifiée');
        finalImageUrl = proxified;
      }
    }

    const wardrobeItem = {
      name:          cleanProductName(product.name),
      category:      product.category || 'Divers',
      color:         product.color   || '',
      brand:         product.brand   || '',
      style:         mapStyle(product),
      emoji:         product.emoji   || '👗',
      image_url:     finalImageUrl,
      ai_tip:        `Acheté sur ${capitalizeFirst(product.partner || 'partenaire')} · Taille ${size}`,
      season:        getCurrentSeason(),
      size,
      partner:       product.partner || '',
      partner_sku:   product.zalandoSku || product.zaraId || product.skimId || product.id || '',
      purchase_price: product.price || 0,
      purchase_date:  new Date().toISOString(),
      source:        'manual_confirm',
    };

    // ═══ FIX 2026-04 : analyse IA AVANT sauvegarde DB ═══
    // Avant : on sauvegardait l'item, puis on lançait l'analyse en background.
    //         → en cas d'échec, item dans le dressing sans analyse → matching
    //           de couleurs / styles incomplet pour les looks suivants.
    // Maintenant : on analyse d'abord. Si OK → on sauvegarde avec metadata.
    //              Si quota dépassé / échec → on N'AJOUTE PAS au dressing.
    let metadata = null;

    // ═══ v1.10.33 : Réutiliser l'analyse catalogue si déjà existante ═══
    //
    //   Quand un user achète depuis le catalogue, le produit est probablement
    //   déjà analysé par le cron `outfit-catalog-analysis-cron` (Claude Vision
    //   tourne 1×/jour, partagé entre tous les users).
    //
    //   Plutôt que de re-analyser pour chaque user (coût + quota IA consommé
    //   inutilement), on récupère l'analyse existante depuis la DB via
    //   POST /catalog-analysis (endpoint backend qui interroge catalog_analysis).
    //
    //   Bénéfices :
    //     - 0 appel Claude Vision pour produits catalogue
    //     - 0 quota IA user consommé (on garde leur quota pour leurs photos perso)
    //     - Réponse instantanée (lecture DB vs 3-5s d'analyse Claude)
    //     - Mais le user voit toujours "Analyse en cours..." dans l'UI (UX préservée)
    //
    //   Si l'analyse n'est pas trouvée en DB (produit pas encore analysé),
    //   fallback sur l'analyse classique via /analyze-item.
    const productPartnerId = product.partner_sku || product.zalandoSku || product.zaraId
                          || product.skimId || product.id;
    if (productPartnerId && product.partner) {
      try {
        onProgress('analyzing');
        const { AWS_CONFIG } = require('../config/aws_config');
        // v1.12.484 — fix : AWS_CONFIG.api était undefined → crash optional chaining.
        const apiUrl = AWS_CONFIG?.api?.baseUrl || AWS_CONFIG?.apiUrl || '';
        if (apiUrl) {
          const r = await _fetchTimeout(`${apiUrl}/catalog-analysis`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${userToken}`,
            },
            body: JSON.stringify({ productIds: [String(productPartnerId)] }),
          }, 12000);
          if (r.ok) {
            const data = await r.json();
            const cached = data?.byId?.[String(productPartnerId)];
            if (cached && typeof cached === 'object') {
              metadata = cached;
              console.log(`[PurchaseConfirm] ✓ analyse réutilisée depuis catalogue (gratuit) : ${productPartnerId}`);
            }
          }
        }
      } catch (e) {
        console.warn('[PurchaseConfirm] catalog-analysis lookup failed:', e?.message);
      }
    }

    // Fallback : si pas trouvé en cache catalogue, analyser via Claude Vision
    // v1.12.268 — _aiCounted flag pour éviter double increment côté caller
    let _aiCounted = false;
    if (!metadata && finalImageUrl && finalImageUrl.startsWith('http')) {
      try {
        const { analyzeItemImage } = await import('./AIStylistService');
        metadata = await analyzeItemImage({
          id:       'preview_' + Date.now(),
          name:     wardrobeItem.name,
          category: wardrobeItem.category,
          image:    finalImageUrl,
        }, userToken);
        _aiCounted = true; // /analyze-item backend a déjà incrémenté ai_usage
      } catch (e) {
        console.warn('[PurchaseConfirm] analyse IA échouée:', e.message);
      }
    }

    // Quota dépassé : on bloque l'ajout
    if (metadata && metadata._quotaExceeded) {
      console.log('[PurchaseConfirm] ⛔ quota IA dépassé, ajout annulé');
      try {
        const { DeviceEventEmitter } = require('react-native');
        DeviceEventEmitter.emit('ai_limit_reached', {
          tier:    metadata._tier || 'free',
          current: metadata._current || 0,
          limit:   metadata._limit || 0,
          feature: 'add_to_wardrobe',
        });
      } catch {}
      return { success: false, error: 'quota_exceeded', _quotaExceeded: true };
    }

    // Analyse a totalement échoué (réseau, timeout, etc.) : on bloque l'ajout
    if (!metadata) {
      console.warn('[PurchaseConfirm] ⛔ analyse IA indisponible, ajout annulé');
      return { success: false, error: 'ai_unavailable' };
    }

    // Photo rejetée (pas un vêtement, photo floue, etc.) : on bloque
    if (metadata._rejected) {
      console.log('[PurchaseConfirm] 🚫 photo rejetée par IA');
      return { success: false, error: 'photo_rejected', _userMessage: metadata._userMessage };
    }

    // Inclure la metadata dans l'item à sauvegarder
    // v1.12.89 — injecter le pseudo user dans ai_metadata pour persistance
    if (userPseudo && metadata && typeof metadata === 'object') {
      metadata.pseudo = userPseudo;
    }
    wardrobeItem.ai_metadata = metadata;

    // Enregistrer dans la DB via Lambda
    const response = await _fetchTimeout(`${API}/wardrobe`, {
      method:  'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${userToken}`,
      },
      body: JSON.stringify(wardrobeItem),
    }, 20000);

    if (response.status === 401) throw new Error('HTTP 401 - Token expiré, reconnectez-vous');
    if (!response.ok)           throw new Error(`HTTP ${response.status}`);

    const raw   = await response.json();
    // Normaliser : DB retourne image_url, WardrobeScreen attend image
    const added = { ...raw, image: raw.image_url || raw.image || finalImageUrl || null };

    // Émettre event pour rafraîchir les écrans dressing avec la nouvelle metadata
    try {
      const { DeviceEventEmitter } = require('react-native');
      DeviceEventEmitter.emit('wardrobe_updated', { aiAnalyzed: 1, itemId: added.id });
    } catch {}

    // v1.13.18 — GA4 : article ajouté au dressing DEPUIS LE CATALOGUE (« J'ai acheté »)
    try {
      const { trackEvent } = require('./AnalyticsService');
      trackEvent('wardrobe_item_added', {
        source: 'catalog',
        category: wardrobeItem?.category || added?.category || 'unknown',
        brand: product?.brand || wardrobeItem?.brand || null,
        partner: product?.partner || product?.partner_id || null,
      });
    } catch {}

    // Enregistrer la commande pour suivi commission (non bloquant)
    recordPurchase(product, size, userToken).catch(e => console.warn('recordPurchase:', e.message));

    // Nettoyer le produit en attente
    await clearPendingProduct();

    return { success: true, item: added, _aiCounted };
  } catch (e) {
    console.error('confirmPurchaseAndAddToWardrobe:', e);
    return { success: false, error: e.message };
  }
}

/* ══════════════════════════════════════════════════════════
   6. ENREGISTRER LA COMMANDE (pour suivi commission)
══════════════════════════════════════════════════════════ */
async function recordPurchase(product, size, userToken) {
  try {
    await fetch(`${API}/purchases`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userToken}`,
      },
      body: JSON.stringify({
        product_id:    product.id,
        product_name:  product.name,
        product_sku:   product.zalandoSku || product.id,
        brand:         product.brand,
        partner:       product.partner,
        price:         product.price,
        size,
        image:         product.image,
        affiliate_url: product.affiliateUrl,
        purchased_at:  new Date().toISOString(),
      }),
    });
  } catch (e) {
    console.error('recordPurchase:', e);
  }
}

/* ── Helpers ────────────────────────────────────────────── */
function cleanProductName(name = '') {
  // "SLEEVELESS MIDI  - Robe longue - dark blue" → "Robe longue dark blue"
  const parts = name.split(' - ');
  if (parts.length >= 2) return parts.slice(1).join(' ').trim();
  return name.trim();
}

function mapStyle(product) {
  const cat = (product.category || '').toLowerCase();
  const name = (product.name || '').toLowerCase();
  if (name.includes('casual') || cat.includes('jean'))  return 'Casual';
  if (name.includes('soirée') || name.includes('robe')) return 'Soirée';
  if (name.includes('sport'))                           return 'Sport';
  return 'Chic';
}

function getCurrentSeason() {
  const month = new Date().getMonth();
  if (month >= 2 && month <= 4)  return 'Printemps';
  if (month >= 5 && month <= 7)  return 'Été';
  if (month >= 8 && month <= 10) return 'Automne';
  return 'Hiver';
}

function capitalizeFirst(str = '') {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
